import { useQuery } from '@apollo/client';
import { AccordionDetails, AccordionSummary, Box, ButtonGroup, styled } from '@mui/material';
import ErrorState from 'Components/ErrorState';
import FlexBox from 'Components/FlexBox';
import Loader from 'Components/Forms/Loader';
import InfiniteScrollWrapper from 'Components/InfiniteScrollWrapper';
import PageSubTitle from 'Components/PageSubTitle';
import { InfoTooltip, ThemeButton, ThemeTypography } from 'DesignSystem';
import { Accordion } from 'DesignSystem/DataDisplay/Accordion/Accordion';
import SummaryBadge from 'DesignSystem/DataDisplay/SummaryBadge/SummaryBadge';
import Icon from 'DesignSystem/Primitives/Icon/Icon';
import {
  EDatasetView,
  EGeoDataIssueSeverityLevel,
  IDataset,
  IGeoDataIssue,
} from 'FeatureModules/DataImport/types/dataset.types';
import PaginatedMap from 'FeatureModules/Site/Components/PaginatedMap';
import PlotItem from 'FeatureModules/Site/Components/PlotItem';
import usePlotsMutations from 'FeatureModules/Site/hooks/usePlotMutations';
import useSiteActions from 'FeatureModules/Site/hooks/useSiteActions';
import { IPlot } from 'FeatureModules/Site/types/plot.types';
import { ESiteViewOption, IBaseSite, IFarmSite } from 'FeatureModules/Site/types/site.types';
import { GET_PLOTS_WITH_FARMS } from 'graphql/secure/queries/plot.queries';
import { GET_SITES } from 'graphql/secure/queries/site.queries';
import { IPlotsFilters, IPlotsOrderBy } from 'graphql/secure/queries/types/plot-query.types';
import {
  ISitesFilters,
  ISitesOrderBy,
  ISiteSummary,
} from 'graphql/secure/queries/types/site-query.types';
import useDialog from 'hooks/useDialog';
import useMessages from 'hooks/useMessages';
import React, { FC, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import useMeasure from 'react-use/lib/useMeasure';
import { IGraphQlConnection } from 'types/core.types';
import { createEnumParam, useQueryParam, withDefault } from 'use-query-params';
import { removeGraphConnections } from 'utils/graphql.utils';
import {
  PLOTS_PER_PAGE,
  PLOTS_PER_PLOT_LIBRARY_PAGE,
  SITES_TABLE_ROWS_PER_PAGE,
} from 'utils/pagination.utils';
import { APP_QUERY_PARAMS } from 'utils/queryParam.utils';
import PlotIssueBanner, { IIssue } from '../PlotIssueBanner';
import SiteItem from '../SiteItem';

type IDatasetVerificationProps = {
  dataset: IDataset;
  issues?: IGeoDataIssue[];
  initialViewOption?: EDatasetView;
  isEditDisabled?: boolean;
  pageSubTitle?: string;
};

const Container = styled(Box)({
  width: '100%',
});

const HintContainer = styled('div')(({ theme }) => ({
  background: theme.custom.themeColors.primary[5],
  borderRadius: theme.spacing(1),
  height: 'fit-content',
  border: `1px solid ${theme.custom.themeColors.primary[40]}`,
}));

const StyledAccordion = styled(Accordion)(() => ({
  '&.MuiAccordion-root': {
    margin: 0,
    border: 'none !important',
  },
  '& .MuiAccordionDetails-root': {
    paddingTop: 0,
    background: 'transparent',
  },
}));

const List = styled('ul')(() => ({
  margin: 0,
  padding: '0 0 0 16px',
}));

const DatasetVerification: FC<IDatasetVerificationProps> = ({
  dataset,
  issues,
  initialViewOption = EDatasetView.MAP,
  isEditDisabled,
  pageSubTitle,
}) => {
  const paginationRef = useRef<{ resetPagination: () => void }>(null);
  const [containerRef, { height: containerHeight }] = useMeasure<HTMLDivElement>();
  const navigate = useNavigate();
  const [viewOption, setViewOption] = useQueryParam(
    APP_QUERY_PARAMS.viewOption,
    withDefault(
      createEnumParam<EDatasetView>([EDatasetView.FARMS, EDatasetView.PLOTS, EDatasetView.MAP]),
      initialViewOption
    ),
    { removeDefaultsFromUrl: true }
  );
  const {
    data: plotsData,
    loading: plotsLoading,
    error: plotsError,
    fetchMore: fetchMorePlots,
  } = useQuery<
    { plots: IGraphQlConnection<IPlot> },
    { filters: IPlotsFilters; first: number; orderBy: IPlotsOrderBy }
  >(GET_PLOTS_WITH_FARMS, {
    variables: {
      filters: {
        ...(dataset && { datasetId: dataset.id }),
      },
      orderBy: {
        orderByField: 'issueSeverityScore',
        orderByMethod: 'DESC',
      },
      first: PLOTS_PER_PAGE,
    },
    notifyOnNetworkStatusChange: true,
    skip: viewOption !== EDatasetView.MAP,
  });

  const {
    data: tablePlotsData,
    loading: tablePlotsLoading,
    error: tablePlotsError,
    fetchMore: fetchMoreTablePlots,
  } = useQuery<{ plots: IGraphQlConnection<IPlot> }>(GET_PLOTS_WITH_FARMS, {
    variables: {
      filters: {
        ...(dataset && { datasetId: dataset.id }),
      },
      orderBy: {
        orderByField: 'issueSeverityScore',
        orderByMethod: 'DESC',
      },
      first: PLOTS_PER_PLOT_LIBRARY_PAGE,
    },
    notifyOnNetworkStatusChange: true,
    skip: viewOption !== EDatasetView.PLOTS,
  });

  const {
    data: farmData,
    loading: farmsLoading,
    error: farmsError,
    fetchMore: fetchMoreFarms,
  } = useQuery<
    { sites: IGraphQlConnection<IFarmSite, ISiteSummary> },
    {
      filters: ISitesFilters;
      orderBy: ISitesOrderBy;
      siteViewOption: ESiteViewOption;
      first: number;
      after?: string | null;
    }
  >(GET_SITES, {
    variables: {
      filters: {
        ...(dataset && { geoDataId: dataset?.originalGeoDataId }),
        ownership: null,
      },
      ...(dataset
        ? {
            summaryFilters: {
              ...(dataset && { geoDataId: dataset?.originalGeoDataId }),
            },
          }
        : {}),
      orderBy: {
        orderByField: 'issueSeverityScore',
        orderByMethod: 'DESC',
      },
      siteViewOption: ESiteViewOption.LIST,
      first: SITES_TABLE_ROWS_PER_PAGE,
    },
    notifyOnNetworkStatusChange: true,
  });

  const { openDialog } = useDialog();
  const { deletePlots } = usePlotsMutations();
  const { setSuccessMessage, setErrorMessage } = useMessages();
  const { handleDeleteSite, handleEditFarmPlots, handleEditSite } = useSiteActions();

  const plots: IPlot[] = useMemo(
    () => (plotsData?.plots ? removeGraphConnections(plotsData.plots) : []),
    [plotsData]
  );
  const hasNextPlotPage: boolean = !!plotsData?.plots?.pageInfo?.hasNextPage;
  const endCursorPlot: string | undefined = plotsData?.plots?.pageInfo?.endCursor;
  const plotsCount: number = plotsData?.plots?.count || 0;

  const tablePlots: IPlot[] = useMemo(
    () => (tablePlotsData?.plots ? removeGraphConnections(tablePlotsData.plots) : []),
    [tablePlotsData]
  );
  const hasNextTablePlotPage: boolean = !!tablePlotsData?.plots?.pageInfo?.hasNextPage;
  const endCursorTablePlot: string | undefined = tablePlotsData?.plots?.pageInfo?.endCursor;
  const tablePlotCount: number = tablePlotsData?.plots?.count || 0;

  const farms: IFarmSite[] = useMemo(
    () => (farmData?.sites ? removeGraphConnections(farmData.sites) : []),
    [farmData]
  );
  const hasNextFarmPage: boolean = !!farmData?.sites?.pageInfo?.hasNextPage;
  const endCursorFarm: string | undefined = farmData?.sites?.pageInfo?.endCursor;
  const farmsCount: number = farmData?.sites?.count || 0;

  const handlePlotPageEndReached = async () => {
    if (endCursorPlot) {
      await fetchMorePlots({
        variables: {
          after: endCursorPlot,
        },
      });
    }
  };

  const handleTablePlotPageEndReached = async () => {
    if (endCursorTablePlot) {
      await fetchMoreTablePlots({
        variables: {
          after: endCursorTablePlot,
        },
      });
    }
  };

  const handleFarmPageEndReached = async () => {
    if (endCursorFarm) {
      await fetchMoreFarms({
        variables: {
          after: endCursorFarm,
        },
      });
    }
  };

  const issuesSummary = useMemo(() => {
    const errors: IIssue[] = [];
    const warnings: IIssue[] = [];
    issues?.forEach(({ code, message, severityLevel }) => {
      const summaryList =
        severityLevel === EGeoDataIssueSeverityLevel.WARNING
          ? warnings
          : severityLevel === EGeoDataIssueSeverityLevel.ERROR
          ? errors
          : [];
      const existingIssue = summaryList.find(e => e.code === code);
      if (existingIssue) {
        existingIssue.count += 1;
      } else {
        summaryList.push({ code, message, count: 1 });
      }
    });
    return { errors, warnings };
  }, [issues]);

  const handleEditPlot = (plot: IPlot) => {
    openDialog({
      type: 'ADD_EDIT_FARM_PLOT',
      props: {
        plot,
      },
    });
  };

  const handleDeletePlot = async (plot: IPlot) => {
    openDialog({
      type: 'ALERT',
      props: {
        title: 'Delete plot',
        text: 'Are you sure you want to delete this plot? All data will be lost and you will not be able to recover the plot.',
        submitText: 'Delete',
        itemTitle: `${plot.title ? `${plot.title} | ` : ''} ${plot.externalId}`,
        displayCloseButton: true,
        onSubmit: () => {
          deletePlots({
            variables: { ids: [plot.id] },
            onCompleted: () => {
              setSuccessMessage('Plot successfully deleted');
            },
            onError: () => {
              setErrorMessage('Failed to delete plot');
            },
          });
        },
        onCancel: () => undefined,
      },
    });
  };

  const handleDownloadClick = () => {
    const geoDataId = dataset?.originalGeoDataId;
    if (!geoDataId) {
      console.error('No geoDataId to download found');
      return;
    }
    openDialog({
      type: 'DOWNLOAD_GEO_DATA',
      props: {
        geoDataId,
      },
    });
  };

  const handleSiteClick = (site: IBaseSite) => navigate(`/sites/${site.id}`);

  const handleFarmPlotClick = (farmPlot: IPlot) =>
    navigate(`/sites/${farmPlot.farm.id}/plots/${farmPlot.id}`);

  if (
    (plotsLoading && !plots.length) ||
    (tablePlotsLoading && !tablePlots.length) ||
    (farmsLoading && !farms.length)
  ) {
    return (
      <Box position="absolute" mt={3} width="100%">
        <Loader />
      </Box>
    );
  }
  if (plotsError || farmsError || tablePlotsError) {
    return <ErrorState />;
  }

  return (
    <Box height="100%" data-testid="geo-data-verification-container">
      <Box ref={containerRef}>
        {(issues ?? []).length > 0 && (
          <Box display="flex" flexDirection="column" gap={1} mb={2}>
            {issuesSummary.errors.length > 0 && (
              <PlotIssueBanner
                issues={issuesSummary.errors}
                issueCount={issuesSummary.errors.reduce((acc, curr) => acc + curr.count, 0)}
                severity="error"
              />
            )}
            {issuesSummary.warnings.length > 0 && (
              <PlotIssueBanner
                issues={issuesSummary.warnings}
                issueCount={issuesSummary.warnings.reduce((acc, curr) => acc + curr.count, 0)}
                severity="warning"
              />
            )}
            <HintContainer>
              <StyledAccordion transparent>
                <AccordionSummary>
                  <ThemeTypography color="ALMOST_BLACK" variant="BODY_MEDIUM_BOLD">
                    You can still save this import with warnings. To address the issues, consider
                    the following recommendations:
                  </ThemeTypography>
                </AccordionSummary>
                <AccordionDetails>
                  <ThemeTypography variant="BODY_SMALL">
                    <List>
                      <li>
                        If you need to export a summary of the geo-data warnings, download the file
                        with highlighted issues below and forward the file to relevant partners.
                      </li>
                      <li>
                        If you are you able to manually adjust plot boundaries on the map, you can
                        edit the cultivation area per plot in this view directly (e.g. edit points
                        and draw polygons).
                      </li>
                      <li>
                        If you have another dataset of the same farm plots but with different
                        polygon geometries, you can save this dataset and later add the new dataset.
                        Changes in the plots will then be updated for the same farm ids.
                      </li>
                      <li>
                        If you prefer to fix the problems in the original file, you can always
                        re-upload again.
                      </li>
                    </List>
                  </ThemeTypography>
                </AccordionDetails>
              </StyledAccordion>
            </HintContainer>
          </Box>
        )}

        <FlexBox justifyContent="space-between" alignItems="center" mb={1}>
          <FlexBox gap={2}>
            {pageSubTitle && <PageSubTitle title={pageSubTitle} />}
            <FlexBox>
              <ButtonGroup>
                <ThemeButton
                  data-testid="geo-data-map-view"
                  className={viewOption === EDatasetView.MAP ? 'selected' : ''}
                  onClick={() => setViewOption(EDatasetView.MAP)}
                  typographyColor="ALMOST_BLACK"
                >
                  Map view
                </ThemeButton>
                <ThemeButton
                  data-testid="geo-data-plot-view"
                  className={viewOption === EDatasetView.PLOTS ? 'selected' : ''}
                  onClick={() => setViewOption(EDatasetView.PLOTS)}
                  typographyColor="ALMOST_BLACK"
                >
                  Plot list view
                </ThemeButton>
                <ThemeButton
                  data-testid="geo-data-farm-view"
                  className={viewOption === EDatasetView.FARMS ? 'selected' : ''}
                  onClick={() => setViewOption(EDatasetView.FARMS)}
                  typographyColor="ALMOST_BLACK"
                >
                  Farm list view
                </ThemeButton>
              </ButtonGroup>
              <InfoTooltip text="Select to view the farm plots in a list or on the map. Both views allows editing the farm data (e.g. farm size) or the farm plot cultivation area." />
            </FlexBox>
          </FlexBox>

          {!!dataset?.originalGeoDataId && (
            <ThemeButton
              startIcon={<Icon name="download" />}
              color="WHITE"
              onClick={handleDownloadClick}
            >
              Download geo-data
            </ThemeButton>
          )}
        </FlexBox>
      </Box>

      <Container height={`calc(100% - ${containerHeight}px)`}>
        {farms.length > 0 && viewOption === EDatasetView.FARMS && (
          <>
            <FlexBox gap={2}>
              <ThemeTypography variant="BODY_MEDIUM" color="GRAY_100">
                Showing farms 1-{farms.length} of {farmsCount}
              </ThemeTypography>
              <SummaryBadge value={farmData?.sites.summary?.farmsCount || 0} name="farm" />
              <SummaryBadge value={farmData?.sites.summary?.plotsCount || 0} name="plot" />
            </FlexBox>

            <Box mt={1}>
              <InfiniteScrollWrapper
                ScrollWrapperComponent={Box} // Current table scroll does not use its own scroll, so we use Box as a simple wrapper and the body is scrolled by the page
                hasMore={hasNextFarmPage}
                next={handleFarmPageEndReached}
              >
                {farms.map(farm => {
                  const issuesOfSite = issues?.filter(issue => issue.siteId === farm.id);
                  return (
                    <SiteItem
                      key={farm.id}
                      site={farm}
                      issues={issuesOfSite || []}
                      onClick={handleSiteClick}
                      onEdit={!isEditDisabled ? handleEditSite : undefined}
                      onEditPlots={!isEditDisabled ? handleEditFarmPlots : undefined}
                      onDelete={!isEditDisabled ? handleDeleteSite : undefined}
                    />
                  );
                })}
              </InfiniteScrollWrapper>
            </Box>
          </>
        )}

        {tablePlots.length > 0 && viewOption === EDatasetView.PLOTS && (
          <>
            <FlexBox gap={2}>
              <ThemeTypography variant="BODY_MEDIUM" color="GRAY_100">
                Showing plots 1-{tablePlots.length} of {tablePlotCount}
              </ThemeTypography>
              <SummaryBadge value={farmData?.sites.summary?.farmsCount || 0} name="farm" />
              <SummaryBadge value={farmData?.sites.summary?.plotsCount || 0} name="plot" />
            </FlexBox>

            <Box mt={1}>
              <InfiniteScrollWrapper
                ScrollWrapperComponent={Box} // Current table scroll does not use its own scroll, so we use Box as a simple wrapper and the body is scrolled by the page
                hasMore={hasNextTablePlotPage}
                next={handleTablePlotPageEndReached}
              >
                {tablePlots.map(plot => {
                  const issuesOfPlot = issues?.filter(
                    issue => issue.entityType === 'PLOT' && issue.entityId === plot.id
                  );
                  return (
                    <PlotItem
                      key={plot.id}
                      plot={plot}
                      issues={issuesOfPlot || []}
                      onClick={handleFarmPlotClick}
                      onEdit={!isEditDisabled ? handleEditPlot : undefined}
                      onDelete={!isEditDisabled ? handleDeletePlot : undefined}
                    />
                  );
                })}
              </InfiniteScrollWrapper>
            </Box>
          </>
        )}

        {plots.length > 0 && viewOption === EDatasetView.MAP && (
          <PaginatedMap
            paginationRef={paginationRef}
            plots={plots}
            hasNextPage={hasNextPlotPage}
            loading={plotsLoading}
            onLoadMorePlots={handlePlotPageEndReached}
            issues={issues}
            hideTitle
            itemCount={plotsCount}
            geoDataId={dataset?.originalGeoDataId}
            tooltipText={`Due to browser capacity, maximum ${PLOTS_PER_PAGE} farm plots can be shown at the same time. Farm plots with warnings are prioritised first and shown on the first page. Click to load the next page of farms.`}
            mode={isEditDisabled ? 'view' : 'edit'}
            TitleSummary={
              <FlexBox gap={2}>
                <SummaryBadge value={farmData?.sites.summary?.farmsCount || 0} name="farm" />
                <SummaryBadge value={farmData?.sites.summary?.plotsCount || 0} name="plot" />
              </FlexBox>
            }
          />
        )}
      </Container>
    </Box>
  );
};
export default DatasetVerification;
